import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { CustomTheme, B2CCard, DottedDivider } from '@masfront/common-b2c'
import { isThisMonth } from 'date-fns'

import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Skeleton from '@material-ui/lab/Skeleton'

import ErrorAndRetry from './ErrorAndRetry'

import { selectAgent } from '~/auth/store/auth.selectors'
import {
  dataSubscriptionTariff,
  voiceSubscriptionTariff,
  getBonusesOutOfTariff,
} from '~/bonus/helpers/bonus.helpers'
import useBonus from '~/bonus/hooks/useBonus'
import { useBrand } from '~/brand'
import { getAccountFromSubscriptionId, getPackageFromProductId } from '~/customer/helpers'
import { Subscription } from '~/customer/model/customer.model'
import useCustomer from '~/customer/useCustomer'
import useDiscount from '~/discounts/useDiscount'
import useSubscriptionDetail from '~/subscriptionDetail/hooks/useSubscriptionDetail'
import LineItem from '~/ui/components/LineItem/LineItem'
import { formatDate, formatBytes, formatMinutes, formatFee } from '~/utils'

const useStyles = makeStyles((theme: CustomTheme) => ({
  lineItem: {
    marginBottom: theme.spacing(2),
  },
}))

type SubscriptionCardProps = {
  subscriptionNonFixed?: Subscription
  subscriptionFixed?: Subscription
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ subscriptionNonFixed }) => {
  const { customer } = useCustomer()
  const classes = useStyles()
  const { brand } = useBrand()
  const { agent } = useSelector(selectAgent)

  const account =
    customer &&
    subscriptionNonFixed &&
    getAccountFromSubscriptionId(customer, subscriptionNonFixed.id)
  const subscriptionNonFixedId = subscriptionNonFixed?.id || ''
  const tariffId = subscriptionNonFixed?.commercialProduct.id || ''
  const provinceId = String(customer && customer.addressInfo.stateId)
  const accountId = account?.id || ''
  const pkg =
    customer &&
    subscriptionNonFixed &&
    getPackageFromProductId(customer, subscriptionNonFixed.identificationNr)
  const bundleType = pkg?.bundle.type

  const {
    getSubscriptionDetail,
    subscriptionDetail,
    loading: subscriptionDetailLoading,
    error: subscriptionDetailError,
  } = useSubscriptionDetail(subscriptionNonFixedId)

  const { getBonus, bonus, loading: bonusLoading, error: bonusError } = useBonus(
    subscriptionNonFixedId,
  )

  const { getDiscount, discount, loading: discountLoading, error: discountError } = useDiscount(
    subscriptionNonFixedId,
  )

  const fetchSubscriptionDetail = () =>
    getSubscriptionDetail({
      subscriptionId: subscriptionNonFixedId,
      include: 'historicalChanges',
      currentMonth: true,
      brand,
      sub: agent?.sub,
    })

  const fetchBonus = () =>
    getBonus({
      subscriptionId: subscriptionNonFixedId,
      tariffId,
      provinceId,
      brand,
      sub: agent?.sub,
    })

  const fetchDiscount = () =>
    getDiscount({
      subscriptionId: subscriptionNonFixedId,
      accountId,
      tariffId,
      provinceId,
      brand,
      sub: agent?.sub,
      bundleType,
    })

  useEffect(() => {
    if (subscriptionNonFixedId) {
      fetchSubscriptionDetail()
    }
  }, [subscriptionNonFixedId])

  useEffect(() => {
    if (subscriptionNonFixedId) {
      fetchBonus()
    }
  }, [subscriptionNonFixedId])

  useEffect(() => {
    if (subscriptionNonFixedId) {
      fetchDiscount()
    }
  }, [subscriptionNonFixedId])

  const skeletonBlock = (width: number) => (
    <Box py={1}>
      <Skeleton animation="wave" variant="text" width={`${width}%`} height={24} />
    </Box>
  )

  const otherActiveTariffs = subscriptionDetail?.commercialProduct.historicalChanges?.filter(
    (historicChange) => isThisMonth(new Date(historicChange.activationDate)),
  )
  const bonusesOutOfTariff = bonus && getBonusesOutOfTariff(bonus)

  return (
    <B2CCard title="Suscripción" dataHookPrefix="subscription">
      {customer && (
        <>
          {subscriptionDetailLoading ? (
            <>
              {skeletonBlock(30)} {skeletonBlock(50)}
            </>
          ) : (
            <LineItem
              dataHook="tariff-subscription-card"
              className={classes.lineItem}
              primaryText="Tarifa"
              secondaryText={subscriptionDetail?.commercialProduct.description}
              subPrimaryText={`(Desde el ${formatDate(
                subscriptionDetail?.commercialProduct.lastActivationDate || '',
              )})`}
              vertical
            />
          )}
          {otherActiveTariffs?.map((tariff) => (
            <LineItem
              key={tariff.tariffId}
              dataHook="tariff-incoming-subscription-card-"
              className={classes.lineItem}
              primaryText="Tarifa nueva"
              secondaryText={tariff.name}
              subPrimaryText={`(Desde el ${formatDate(tariff.activationDate)})`}
              vertical
            />
          ))}

          {subscriptionDetailError && <ErrorAndRetry action={fetchSubscriptionDetail} />}

          {bonusLoading ? (
            <>
              {skeletonBlock(30)} {skeletonBlock(50)} {skeletonBlock(100)}
            </>
          ) : (
            <>
              <LineItem
                dataHook="totalData-subscription-card"
                className={classes.lineItem}
                primaryText="Total internet"
                secondaryText={
                  bonus?.length ? String(formatBytes(dataSubscriptionTariff(bonus))) : '-'
                }
              />
              <LineItem
                dataHook="totalVoiceSubscription-subscription-card"
                className={classes.lineItem}
                primaryText="Total llamadas"
                secondaryText={
                  bonus?.length ? String(formatMinutes(voiceSubscriptionTariff(bonus))) : '-'
                }
              />
            </>
          )}
          <Box m={1.5} />
          <DottedDivider />
          <Box m={2} />
          <Typography variant="subtitle1">Bonos y ampliaciones</Typography>
          <Box m={2} />

          {bonusLoading && (
            <>
              {skeletonBlock(30)} {skeletonBlock(50)} {skeletonBlock(100)}
            </>
          )}
          {!bonusLoading &&
            !bonusError &&
            bonusesOutOfTariff &&
            (bonusesOutOfTariff?.length ? (
              bonusesOutOfTariff.map((bonus) => (
                <LineItem
                  key={bonus.bonusType.id}
                  dataHook={`bonus-${bonus.bonusType.id}-subscription-card`}
                  className={classes.lineItem}
                  primaryText={`${bonus.bonusType.name}`}
                  subPrimaryText={`(${formatDate(bonus.startDate)} ${
                    bonus.bonusType.isUnlimitedDuration
                      ? 'a Ilimitado)'
                      : `al ${formatDate(bonus.endDate)})`
                  }
                `}
                  isHeader
                  vertical
                />
              ))
            ) : (
              <Box my={2} fontStyle="italic">
                <Typography variant="body2">No tiene ninguno activo</Typography>
              </Box>
            ))}

          {bonusError && <ErrorAndRetry action={fetchBonus} />}

          <Box m={2} />
          <DottedDivider />
          <Box m={2} />
          <Typography variant="subtitle1">Descuentos</Typography>
          <Box m={2} />

          {discountLoading && (
            <>
              {skeletonBlock(30)} {skeletonBlock(70)}
            </>
          )}

          {!discountLoading &&
            !discountError &&
            (discount?.length ? (
              discount.map((discount) => (
                <LineItem
                  key={discount.promotionId}
                  dataHook={`discount-${discount.promotionId}-subscription-card`}
                  className={classes.lineItem}
                  primaryText={`${discount.promotionName} ${
                    // eslint-disable-next-line no-nested-ternary
                    discount.fee || discount.amount
                      ? discount.type === 'PERCENTAGE'
                        ? `${discount.amount?.value}%`
                        : `${formatFee(discount?.fee?.valueWithTax || 0)}`
                      : ''
                  }`}
                  subPrimaryText={`(${formatDate(discount.startDate)} al ${formatDate(
                    discount.endDate,
                  )})`}
                  isHeader
                  vertical
                />
              ))
            ) : (
              <Box my={2} fontStyle="italic">
                <Typography variant="body2">No tiene ninguno activo</Typography>
              </Box>
            ))}

          {discountError && <ErrorAndRetry action={fetchDiscount} />}
        </>
      )}
    </B2CCard>
  )
}

export default SubscriptionCard
